import type { WorkEntryIntegration, ToolCallDescription } from "./workEntryStep";

/** Bundled Chrome mark for paired-browser automation steps. */
export const MORROW_BROWSER_LOGO_URL = "/browser-chrome.webp";

const COMPOSIO_LOGO = (slug: string) => `https://logos.composio.dev/api/${slug}`;

function normalizeToolToken(toolName: string): string {
  return toolName
    .replace(/^\[subagent\]\s*/i, "")
    .trim()
    .toLowerCase();
}

function humanizeTail(raw: string): string {
  const value = raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  if (!value) {
    return "";
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

const EMAIL_TOOL_ACTIONS: Readonly<Record<string, string>> = {
  read_email: "Read email",
  search_email: "Search email",
  send_email: "Send email",
};

const WHATSAPP_INTEGRATION: WorkEntryIntegration = { slug: "whatsapp", name: "WhatsApp" };

const VAULT_TOOL_ACTIONS: Readonly<Record<string, string>> = {
  list_vault_items: "List files",
  search_vault: "Search",
  read_vault_item: "Read",
  get_vault_item_link: "Share link",
  make_attachment_public: "Make public",
};

/**
 * MorrowOS meta-tools (email, browser, WhatsApp, vault, …) that never appear as
 * Composio `GMAIL_*` slugs but still need human labels + icons in the timeline.
 */
export function describeBuiltInMorrowTool(toolName: string): ToolCallDescription | null {
  const cleaned = normalizeToolToken(toolName);
  if (!cleaned) {
    return null;
  }

  const emailAction = EMAIL_TOOL_ACTIONS[cleaned];
  if (emailAction) {
    return {
      appName: null,
      action: emailAction,
      logoUrl: COMPOSIO_LOGO("gmail"),
      slug: "email",
    };
  }

  if (cleaned.startsWith("browser_")) {
    const tail = cleaned.slice("browser_".length);
    const action = humanizeTail(tail) || "Browse";
    return {
      appName: "Browser",
      action,
      logoUrl: MORROW_BROWSER_LOGO_URL,
      slug: "browser",
    };
  }

  if (cleaned.startsWith("whatsapp_")) {
    const tail = cleaned.slice("whatsapp_".length);
    return {
      appName: "WhatsApp",
      action: humanizeTail(tail) || "Message",
      logoUrl: COMPOSIO_LOGO("whatsapp"),
      slug: "whatsapp",
    };
  }

  const vaultAction = VAULT_TOOL_ACTIONS[cleaned];
  if (vaultAction) {
    return {
      appName: "Vault",
      action: vaultAction,
      logoUrl: null,
      slug: "vault",
    };
  }

  if (cleaned === "recall_memory" || cleaned === "search_knowledge") {
    return {
      appName: "Morrow",
      action: humanizeTail(cleaned.replace(/^(recall|search)_/, "")) || "Remember",
      logoUrl: null,
      slug: "morrow_memory",
    };
  }

  if (cleaned === "search_items") {
    return {
      appName: "Morrow",
      action: "Search items",
      logoUrl: null,
      slug: "morrow_items",
    };
  }

  if (cleaned === "show_item_image" || cleaned === "show_item_images") {
    return {
      appName: null,
      action: cleaned === "show_item_image" ? "Show image" : "Show images",
      logoUrl: null,
      slug: "show_item",
    };
  }

  if (cleaned === "generate_image") {
    return {
      appName: null,
      action: "Generate image",
      logoUrl: null,
      slug: "generate_image",
    };
  }

  if (cleaned === "generate_pdf") {
    return {
      appName: null,
      action: "Generate PDF",
      logoUrl: null,
      slug: "generate_pdf",
    };
  }

  if (cleaned === "request_app_connection") {
    return {
      appName: null,
      action: "Connect app",
      logoUrl: null,
      slug: "request_app_connection",
    };
  }

  if (cleaned === "activate_tools") {
    return {
      appName: null,
      action: "Activate tools",
      logoUrl: null,
      slug: "activate_tools",
    };
  }

  return null;
}

/** Integration row for work-log steps that only carry a built-in tool slug. */
export function resolveBuiltInWorkEntryIntegration(toolName: string): WorkEntryIntegration | null {
  const cleaned = normalizeToolToken(toolName);
  if (!cleaned) {
    return null;
  }

  if (EMAIL_TOOL_ACTIONS[cleaned]) {
    return { slug: "gmail", name: "Email" };
  }

  if (cleaned.startsWith("browser_")) {
    return { slug: "browser", name: "Browser" };
  }

  if (cleaned.startsWith("whatsapp_")) {
    return WHATSAPP_INTEGRATION;
  }

  if (VAULT_TOOL_ACTIONS[cleaned]) {
    return { slug: "vault", name: "Vault" };
  }

  return null;
}
